<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from 'xterm-addon-fit'
import { v4 as uuidv4 } from 'uuid'
import '@xterm/xterm/css/xterm.css'

import { useIdeStore } from '@/stores/ide.store'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'

import { IDEVIEWTABS, TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'

import {
  createWebSocketService,
  connectReceivers,
  sendMessage,
  disconnect
} from '@/services/terminal.service'

import terminalIcon from '@/assets/images/ide/terminalIcon.svg'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import { TerminalMsg } from '@/components/organisation/interface/IAdvanceCompiler'

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.MAXIMIZE
  }
})

const ideStore = useIdeStore()
const advCompilerStore = useAdvanceCompilerStore()

const resizeObserver = ref<ResizeObserver | null>(null)
const width = ref<number>(0)

const command = ref<string>('')
// let inputBuffer = ref<string>('')
// const history = ref<string[]>([])
// let historyIndex = ref<number>(-1)

const splitIdeTabPosition = computed(() => {
  if (props.tabPosition === TAB_BUTTON_POSITION.RIGHT) {
    return 'splitIdeTabRight'
  }
  if (props.tabPosition === TAB_BUTTON_POSITION.BOTTOM) {
    return 'splitTabButtomBar'
  }
  // props.tabPosition === TAB_BUTTON_POSITION.MAXIMIZE
  return `${IDEVIEWTABS.TERMINAL}-main`
})

const isDark = computed(() => ideStore?.colorMode === 'dark')

const terminalTheme = computed(() => {
  return {
    background: !isDark.value ? '#ffffff' : '#192330',
    foreground: !isDark.value ? '#000000' : '#ffffff',
    cursor: '#FD5200',
    selectionBackground: !isDark.value ? '#d3d3d3' : '#6c6d6c'
  }
})

const isPodInitialized = computed(() => {
  return advCompilerStore.projectIdAdvCompiler && advCompilerStore.isPodInitializationCompleted
})

const activeTerminal = computed(() => {
  return advCompilerStore.activeTerminal
})

const terminals = computed(() => {
  return advCompilerStore.terminals
})

const terminalsLength = computed(() => {
  return advCompilerStore.terminals.length
})

/**
 * @description Creates a new Terminal instance with a FitAddon for resizing capabilities.
 * @returns new terminal & fitAddon instances
 */
const createNewTerminal = () => {
  const terminal = new Terminal({
    convertEol: true,
    cursorBlink: true,
    theme: terminalTheme.value
  })
  const fitAddon = new FitAddon()
  terminal.loadAddon(fitAddon)

  terminal.attachCustomKeyEventHandler((e: KeyboardEvent) => {
    // Ctrl + C
    if (e.ctrlKey && e.code === 'KeyC' && e.type === 'keydown') {
      const selection = terminal.getSelection()
      if (selection) {
        navigator.clipboard.writeText(selection)
        return false
      }
    }

    // Ctrl + V
    if (
      !terminals.value[activeTerminal.value].readonly &&
      e.ctrlKey &&
      e.code === 'KeyV' &&
      e.type === 'keydown'
    ) {
      navigator.clipboard.readText().then((text) => {
        terminal.write(text)
        command.value += text.replace(/\r\n/g, '\n')
      })
    }

    return true
  })

  return { terminal, fitAddon }
}

/**
 * Handles user input for the terminal, including command execution, socket reconnection,
 * and special key behaviors like Enter, Backspace, and Ctrl+C.
 * @param data - The input data entered by the user.
 */
function handleInput(data: string) {
  const index = activeTerminal.value
  let xterm = terminals.value[index].xterm
  let socket = terminals.value[index].socket

  if (!xterm) return

  // Ctrl + C
  if (data === '\u0003') {
    if (socket.readyState === WebSocket.CLOSED) {
      socket = createWebSocketService()
      advCompilerStore.setActiveTerminalSocketInstance(socket)
      connectReceivers(activeTerminal.value, true)
    }

    sendMessage(socket, TerminalMsg.INTERRUPT)
    advCompilerStore.setReadOnlyTerminal(index, true)
    xterm.options.cursorBlink = false
  }

  if (terminals.value[activeTerminal.value].readonly) return

  switch (data) {
    case '\r': // Enter
      xterm.write('\r\n')
      processCommand(command.value, xterm)
      if (socket.readyState === WebSocket.CLOSED) {
        socket = createWebSocketService()
        advCompilerStore.setActiveTerminalSocketInstance(socket)
        connectReceivers(activeTerminal.value, true)
      }

      sendMessage(socket, command.value + '\n')
      command.value = ''
      advCompilerStore.setReadOnlyTerminal(index, true)
      xterm.options.cursorBlink = false
      break
    case '\u007F': // Backspace (DEL)
      if (command.value.length > 0) {
        const cursorX = xterm.buffer.active.cursorX
        const cursorY = xterm.buffer.active.cursorY
        if (cursorX === 0 && cursorY > 0) {
          const prevLine = xterm.buffer.active.getLine(cursorY - 1)
          if (prevLine) {
            const row = cursorY
            const column = prevLine.translateToString().trimEnd().length + 1
            xterm.write(`\x1b[${row};${column}H`)
          }
        } else {
          xterm.write('\b \b')
        }
        command.value = command.value.slice(0, -1)
      }
      break
    case '\x1b': // ESC
      xterm.blur()
      break
    case '\x1b[A': // Arrow up
      // const line = xterm.buffer.active
      //   .getLine(xterm.buffer.active.cursorY)
      //   ?.translateToString()
      //   ?.trim() as string

      // // Existing logic for handling arrow up
      // xterm.write('\r' + line)
      // if (historyIndex.value > 0) {
      //   historyIndex.value--
      //   inputBuffer.value = history.value[historyIndex.value]
      //   xterm.write('\r' + line + inputBuffer.value)
      // }
      break
    case '\x1b[B': // Arrow down
      // console.log(
      //   xterm.buffer.active.getLine(xterm.buffer.active.cursorY)?.translateToString(),
      //   xterm.buffer.active.cursorX
      // )
      // xterm.write('\r' + '$ ')
      // if (historyIndex.value < history.value.length - 1) {
      //   historyIndex.value++
      //   inputBuffer.value = history.value[historyIndex.value]
      //   xterm.write('\r' + '$ ' + inputBuffer.value)
      // } else if (historyIndex.value === history.value.length - 1) {
      //   historyIndex.value++
      //   inputBuffer.value = ''
      //   xterm.write('\r' + '$ ')
      // }
      break
    default: // All other characters
      if (
        (data >= String.fromCharCode(0x20) && data <= String.fromCharCode(0x7e)) ||
        data >= '\u00a0'
      ) {
        command.value += data
        xterm.write(data)
      }
  }
}

/**
 * @param command - The command entered by the user in the terminal
 * @param xterm - The terminal instance to process the command for
 */
const processCommand = (command: string, xterm: Terminal) => {
  if (!xterm) return

  if (command.trim() !== '') {
    advCompilerStore.appendActiveTerminalHistory(`${command}\r\n`)
    // history.value.push(command)
    // historyIndex.value = history.value.length
  }

  if (command === 'clear') {
    xterm.clear()
    advCompilerStore.resetActiveTerminalHistory()
  }
}

/**
 * Create New Terminal on click of plus or on initial load
 */
const createNewWindow = () => {
  const { terminal, fitAddon } = createNewTerminal()

  const socket = createWebSocketService()

  advCompilerStore.appendTerminal({
    xterm: terminal,
    id: uuidv4(),
    fitAddon,
    history: [],
    socket,
    readonly: false
  })

  terminal.onData((data) => handleInput(data))

  nextTick(() => {
    hideTerminal(activeTerminal.value)
    advCompilerStore.setActiveTerminal(terminalsLength.value - 1)
    advCompilerStore.setReadOnlyTerminal(terminalsLength.value - 1, true)
    openTerminal(terminalsLength.value - 1)
    connectReceivers(terminalsLength.value - 1)
  })
}

/**
 * Resize active terminal using index and fitAddon
 */
const resizeActiveTerminal = () => {
  const element = document.getElementById(splitIdeTabPosition.value)
  width.value = element?.offsetWidth ?? 0
  const index = activeTerminal.value
  terminals.value[index]?.fitAddon?.fit()
}

/**
 * @param index open ith terminal
 */
const openTerminal = (index: number) => {
  nextTick(() => {
    const terminalWrapper = document.getElementById(`terminal-${index}`)
    if (terminalWrapper) {
      terminals.value[index].xterm.open(terminalWrapper)

      if (!terminals.value[index].readonly) {
        terminals.value[index].xterm.focus()
      } else {
        terminals.value[index].xterm.blur()
      }

      terminals.value[index].xterm.options.cursorBlink = !terminals.value[index].readonly

      resizeActiveTerminal()
    }
  })
}

/**
 * @param index hide ith terminal & update that tab with new instance
 */
const hideTerminal = (index: number) => {
  nextTick(() => {
    command.value = ''

    terminals.value[index].xterm.dispose()

    const { terminal, fitAddon } = createNewTerminal()

    terminal.onData((data) => handleInput(data))

    advCompilerStore.setIthTerminalXtermAndFitAddonInstance(index, terminal, fitAddon)

    terminals.value[index].history.forEach((line) => {
      terminal.write(line)
    })
  })
}

/**
 * Closes the terminal at the specified index.
 * @param index - Index of the terminal to close.
 */
const closeTerminal = (index: number) => {
  const isActiveTerminalBeingClosed = activeTerminal.value === index
  const isLastTerminalBeingClosed = index === terminalsLength.value - 1
  const isActiveTerminalLast = activeTerminal.value === terminalsLength.value - 1

  // Dispose of terminal resources
  terminals.value[index].xterm.dispose()
  disconnect(terminals.value[index].socket)
  advCompilerStore.deleteTerminal(index)

  nextTick(() => {
    if (isActiveTerminalBeingClosed) {
      // If closing the active terminal, set the next active terminal
      const newActiveIndex = isLastTerminalBeingClosed ? index - 1 : index
      advCompilerStore.setActiveTerminal(newActiveIndex)
      openTerminal(newActiveIndex)
    } else if (isActiveTerminalLast) {
      // If the active terminal is the last one, shift active terminal backward
      advCompilerStore.setActiveTerminal(activeTerminal.value - 1)
    } else if (index < activeTerminal.value) {
      // If a terminal before the active one is closed, adjust the active terminal
      advCompilerStore.setActiveTerminal(activeTerminal.value - 1)
      openTerminal(activeTerminal.value - 1)
    }
  })
}

/**
 * @param index hide previous terminal & open new on click of open terminal
 */
const handleTerminalTabClick = (index: number) => {
  hideTerminal(activeTerminal.value)
  advCompilerStore.setActiveTerminal(index)
  openTerminal(index)
}

/**
 * Clear Active Terminal History
 */
const clearTerminalHistory = () => {
  const index = activeTerminal.value
  terminals.value[index].xterm.clear()
  const line = terminals.value[index].xterm.buffer.active.getLine(0)?.translateToString()?.trim()
  advCompilerStore.resetActiveTerminalHistory()
  if (line) {
    advCompilerStore.appendActiveTerminalHistory(line + ' ')
  }
}

watch(isDark, () => {
  const index = activeTerminal.value

  if (terminals.value[index]) {
    terminals.value[index].xterm.options.theme = terminalTheme.value
  }

  terminals.value.forEach((terminal, idx) => {
    if (idx !== index) {
      terminal.xterm.options.theme = terminalTheme.value
    }
  })
})

watch(
  () => isPodInitialized.value,
  (init) => {
    if (init && terminalsLength.value === 0) {
      createNewWindow()
    }
  },
  {
    immediate: true
  }
)

onMounted(() => {
  resizeObserver.value = new ResizeObserver(resizeActiveTerminal)
  const element = document.getElementById(splitIdeTabPosition.value)
  resizeObserver.value?.observe(element as HTMLElement)

  for (let index = 0; index < terminalsLength.value; index++) {
    if (index === activeTerminal.value) {
      handleTerminalTabClick(index)
    } else {
      hideTerminal(index)
    }
  }
})

onBeforeUnmount(() => {
  const element = document.getElementById(splitIdeTabPosition.value)
  resizeObserver.value?.unobserve(element as HTMLElement)
  terminals.value.forEach(({ xterm }) => xterm.dispose())
})
</script>

<template>
  <div class="relative flex h-full w-full flex-col">
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="Terminal"
      :icon="terminalIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.TERMINAL"
    />

    <div
      v-if="isPodInitialized"
      class="background-quaternary relative flex h-full w-full flex-col overflow-hidden"
      :id="`${IDEVIEWTABS.TERMINAL}-main`"
      :style="
        props.tabPosition !== TAB_BUTTON_POSITION.MAXIMIZE &&
        `width: calc(${width}px - ${
          props.tabPosition === TAB_BUTTON_POSITION.RIGHT ? '2px' : '0px'
        })`
      "
    >
      <div class="flex">
        <button class="pl-3.5 outline-none" @click="createNewWindow">
          <FontAwesomeIcon icon="fa-plus" class="color-secondary-500" />
        </button>
        <div class="flex-grow overflow-x-auto">
          <div
            class="stroke-input-default flex min-w-full max-w-0 flex-row overflow-x-auto border-b"
          >
            <button
              v-for="({ id }, index) in terminals"
              :key="id"
              class="whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium outline-none transition"
              :class="
                activeTerminal === index
                  ? 'text-high stroke-input-emphasized'
                  : 'text-mid border-transparent'
              "
              @click="handleTerminalTabClick(index)"
            >
              Terminal {{ index + 1 }}
              <FontAwesomeIcon
                icon="fa-close"
                class="mx-0.5"
                :class="terminals.length === 1 && 'hidden'"
                @click.stop="closeTerminal(index)"
              />
            </button>
          </div>
        </div>
      </div>

      <div
        v-for="(terminal, index) in terminals"
        :key="terminal.id"
        :id="`terminal-${index}`"
        class="terminal-wrapper relative flex-grow overflow-hidden"
        :class="activeTerminal !== index && 'hidden'"
      >
        <button
          @click="clearTerminalHistory"
          class="hs-tooltip-toggle hs-tooltip background-secondary group absolute right-2 top-2 z-10 inline-block rounded-full px-2.5 py-1 [--placement:left]"
        >
          <FontAwesomeIcon
            icon="fa-trash"
            class="icon-ide-default group-hover:icon-ide-emphasized h-3.5 w-3.5"
          ></FontAwesomeIcon>
          <span
            class="hs-tooltip-content invisible absolute inline-block rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-sm transition-opacity hs-tooltip-shown:visible hs-tooltip-shown:opacity-100 dark:bg-slate-700"
            role="tooltip"
          >
            Clear History
          </span>
        </button>
      </div>
    </div>
    <!-- Loader -->
    <div
      v-else
      class="background-quaternary flex h-full min-h-72 items-center justify-center space-x-2"
    >
      <FontAwesomeIcon spin icon="fa-spinner" class="h-4 w-4" />
      <span class="text-mid font-medium">Loading....</span>
    </div>
  </div>
</template>
<style>
.terminal-wrapper .terminal {
  height: 100%;
}
</style>
